import React, { useRef, useState, useEffect, useCallback } from 'react'
import { map, flow, isEmpty, isEqual } from 'lodash'
import InfiniteScroll from 'react-infinite-scroller'
import TopicCard from '../topics/Card'
import ConversationCard from '../conversations/Card'
import { connectTopicTheme } from '../../connectTheme'
import CardLoadingAnimation from './CardLoadingAnimation'
import withTranslation from '../hocs/withTranslation'

export function InfiniteLocationList({
  items,
  itemsLoading,
  onLoadMore,
  limit = 9,
  itemType,
  header,
  slug,
  t,
  filters: Filters,
  filtersProps,
  trailingItem,
  sectionClass,
  transparentHeader,
  pageStart = 0,
  locationSlug
}) {
  const [list, setList] = useState([])
  const [hasMoreItems, setHasMoreItems] = useState(true)

  const loadingRef = useRef(false)
  const pageCountRef = useRef(1)
  const prevItemsRef = useRef([])

  // Sync list when items prop changes
  useEffect(() => {
    if (!isEqual(prevItemsRef.current, items)) {
      const prevCount = prevItemsRef.current.length
      const newCount = items.length

      // If items was reset to empty (filter change), allow loading to restart
      const wasReset = newCount === 0 && prevCount > 0
      const moreItems = wasReset || (newCount > prevCount && (newCount - prevCount) >= limit)

      setList([...items])
      setHasMoreItems(moreItems)
      loadingRef.current = false
      if (wasReset) pageCountRef.current = 1
      prevItemsRef.current = items || []
    }
  }, [items, limit])

  const handleScroll = useCallback(() => {
    if (loadingRef.current) return
    loadingRef.current = true

    const offset = limit * (pageCountRef.current - 1)
    pageCountRef.current += 1

    onLoadMore(offset, limit)
  }, [onLoadMore, limit])

  if (items == null) return null

  const headerClass = transparentHeader ? 'transparent-header-section' : 'header-section'

  const Item = ({ item }) => {
    if (itemType === 'conversation') {
      return <ConversationCard slug={slug} {...item} conversation={item} key={item.id} />
    } else if (itemType === 'topic') {
      return <TopicCard topic={item} key={item.id} />
    }
    return null
  }

  const List = () => {
    if (isEmpty(list) && !itemsLoading && !trailingItem)
      return global.coco || itemType === 'topic' ? (
        <h2 className='align-center'>{t('common.noItemsTopics')}</h2>
      ) : (
        <h2 className='align-center'>{t('common.noItemsConversations')}</h2>
      )
    return (
      <div className='row'>
        {list.map((item, index) => (
          <Item item={item} key={index} />
        ))}
        {trailingItem && trailingItem()}
      </div>
    )
  }

  const headerSection = () => (
    <section className={`pane thin-pane ${headerClass}`}>
      <div className='row center-xs'>
        <h2 className='header-32 header-24-sm flat-bottom'>{header}</h2>
      </div>
      {Filters && <Filters {...filtersProps} />}
    </section>
  )

  return (
    <div>
      {header && headerSection()}

      <section className={`pane reg-pane clear-pane ${sectionClass}`}>
        <InfiniteScroll loadMore={handleScroll} hasMore={hasMoreItems && !itemsLoading} threshold={2000} pageStart={pageStart}>
          <List />
          {itemsLoading ? (
            <div key="loading">
              <CardLoadingAnimation />
            </div>
          ) : null}
        </InfiniteScroll>
      </section>
    </div>
  )
}

export default flow(connectTopicTheme, withTranslation)(InfiniteLocationList)
